import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { BookOpen, Image, CheckCircle, Flame, BarChart3, Shield, Wallet, CircleDollarSign, Lock, ChevronLeft, Scan, Settings, Droplets, Dog, Trophy, Share2, Rocket, Gift } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cleanPageTransition, isWalletBrowser, isMobile } from "@/utils/mobile-compatibility";

export function BlogLayout() {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [buttonLayout, setButtonLayout] = useState<'Top' | 'Bottom'>(
    (localStorage.getItem('buttonLayout') as 'Top' | 'Bottom') || 'Top'
  );

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail === 'Top' || e.detail === 'Bottom') setButtonLayout(e.detail);
    };
    window.addEventListener('buttonLayoutChanged', handler as EventListener);
    return () => window.removeEventListener('buttonLayoutChanged', handler as EventListener);
  }, []);
  
  const handleShare = async () => {
    const shareData = {
      title: 'MdbApp Token Creator',
      text: 'Create your own token on Ethereum, BSC, or Avalanche with MdbApp Token Creator',
      url: 'https://mdbapp.io'
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Thanks for sharing!');
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
      toast.error('Unable to share. Please try again.');
    }
  };

  useEffect(() => {
    console.log('Blog route changed:', location.pathname);
    
    // Apply special handling for mobile wallet browsers
    if (isMobile && isWalletBrowser()) {
      console.log('Applying mobile wallet browser page transition fix for blog');
      cleanPageTransition();
      
      // Force scroll to top on route change
      window.scrollTo(0, 0);
      
      // Add a small delay to ensure the page is fully rendered
      const timer = setTimeout(() => {
        // Force a reflow to ensure the page is properly displayed
        document.body.style.opacity = '0.99';
        setTimeout(() => {
          document.body.style.opacity = '';
        }, 0);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Handle click outside navigation
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const nav = document.getElementById('blog-nav');
      if (nav && !nav.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Collapse nav when route changes
  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);
  
  const blogTabs = [
    {
      title: "Create Token Guide",
      path: "/blog",
      icon: <BookOpen className="w-4 h-4 text-yellow-500" />,
    },
    {
      title: "Create Meme Coin Guide",
      path: "/blog/create-meme-coin",
      icon: <Dog className="w-4 h-4 text-orange-500" />,
    },
    {
      title: "Create Reflection Token",
      path: "/blog/create-reflection-token",
      icon: <CircleDollarSign className="w-4 h-4 text-green-500" />,
    },
    {
      title: "Create Auto-Liquidity Token",
      path: "/blog/create-auto-liquidity-token",
      icon: <Droplets className="w-4 h-4 text-blue-500" />,
    },
    {
      title: "Create Auto-Burn Token",
      path: "/blog/create-auto-burn-token",
      icon: <Flame className="w-4 h-4 text-red-500" />,
    },
    {
      title: "Token Economics",
      path: "/blog/token-economics",
      icon: <BarChart3 className="w-4 h-4 text-purple-500" />,
    },
    {
      title: "Token Security",
      path: "/blog/token-security",
      icon: <Shield className="w-4 h-4 text-green-500" />,
    },
    {
      title: "Automatic Contract Verification",
      path: "/blog/automatic-contract-verification",
      icon: <CheckCircle className="w-4 h-4 text-blue-500" />,
    },
    {
      title: "Matrix Contract Scanner",
      path: "/blog/matrix-contract-scanner",
      icon: <Scan className="w-4 h-4 text-red-500" />,
    },
    {
      title: "Token Tools",
      path: "/blog/token-tools",
      icon: <Settings className="w-4 h-4 text-yellow-500" />,
    },
    {
      title: "Contract Locker",
      path: "/blog/contract-locker",
      icon: <Lock className="w-4 h-4 text-blue-500" />,
    },
    {
      title: "Smart Contract Locking",
      path: "/blog/smart-contract-locking",
      icon: <Lock className="w-4 h-4 text-green-500" />,
    },
    {
      title: "Add Token Logo",
      path: "/blog/add-token-logo",
      icon: <Image className="w-4 h-4 text-purple-500" />,
    },
    {
      title: "Launch on PancakeSwap",
      path: "/blog/launch-on-pancakeswap",
      icon: <Wallet className="w-4 h-4 text-yellow-500" />,
    },
    {
      title: "Best Cold Storage Wallets",
      path: "/blog/best-cold-storage-wallets",
      icon: <Shield className="w-4 h-4 text-blue-500" />,
    },
    {
      title: "Best Crypto Project App 2025",
      path: "/blog/best-crypto-project-app-2025",
      icon: <Trophy className="w-4 h-4 text-yellow-500" />,
    },
    {
      title: "Create SPL Tokens on Solana",
      path: "/blog/solana-token-launcher",
      icon: <Rocket className="w-4 h-4 text-purple-500" />,
    },
    {
      title: "Get Free Solana Tokens",
      path: "/blog/free-solana-tokens",
      icon: <Gift className="w-4 h-4 text-green-500" />,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Blog Tabs Navigation */}
      <div 
        id="blog-nav"
        className={cn(
          "fixed left-0 bottom-0 bg-background/50 backdrop-blur-sm border-r border-[#0A74FF]/10 z-50 overflow-y-auto transition-all duration-300",
          buttonLayout === 'Bottom'
            ? "top-[calc(env(safe-area-inset-top)+var(--main-header-height))]"
            : "top-[calc(7.2rem+env(safe-area-inset-top))] md:top-[calc(7.5rem+env(safe-area-inset-top))] lg:top-[calc(8rem+env(safe-area-inset-top))]",
          isExpanded ? "w-64" : "w-14"
        )}
      >
        {/* Navigation content */}
        <nav 
          className={cn(
            "flex flex-col space-y-2 pt-2",
            isExpanded ? "p-4" : "p-2"
          )}
          aria-label="Tabs"
        >
          {blogTabs.map((tab) => {
            const isActive = location.pathname === tab.path;
            
            return (
              <Button
                key={tab.title}
                asChild
                variant="ghost"
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors border-2 overflow-hidden h-auto",
                  isActive
                    ? "bg-[#0A74FF]/10 text-[#FFD700] border-[#0A74FF]"
                    : "text-muted-foreground hover:text-[#FFD700] hover:bg-[#0A74FF]/5 border-transparent"
                )}
              >
                <Link
                  to={tab.path}
                  className="flex items-center gap-2"
                  title={tab.title}
                >
                  <div className="flex-shrink-0">
                    {tab.icon}
                  </div>
                  <span className={cn(
                    "transition-all duration-300",
                    isExpanded ? "opacity-100 w-auto" : "opacity-0 w-0 hidden"
                  )}>
                    {tab.title}
                  </span>
                </Link>
              </Button>
            );
          })}
        </nav>
      </div>
      {/* Toggle Button - Now outside and taller */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "fixed z-50 top-1/2 -translate-y-1/2 bg-background/50 backdrop-blur-sm hover:bg-[#0A74FF]/10 transition-all duration-300 h-24 flex items-center border border-[#0A74FF]/10",
          isExpanded ? "left-64" : "left-14",
          "px-1 rounded-r-lg"
        )}
      >
        <ChevronLeft className={cn(
          "w-5 h-5 text-[#0A74FF] transition-transform duration-300",
          !isExpanded && "rotate-180"
        )} />
      </button>

      <main className={cn(
        "flex-1 pb-20 transition-all duration-300",
        buttonLayout === 'Bottom'
          ? "pt-[calc(env(safe-area-inset-top)+var(--main-header-height))]"
          : "pt-[calc(7.2rem+env(safe-area-inset-top))] md:pt-[calc(7.5rem+env(safe-area-inset-top))] lg:pt-[calc(8rem+env(safe-area-inset-top))]",
        isExpanded ? "pl-64" : "pl-14"
      )}>
        <div className="container mx-auto px-8">
          <Outlet />
          {/* QR Code Section */}
          <div className="flex flex-col items-center justify-center mt-16 mb-8 border-t border-[#0A74FF]/10 pt-12">
            <div className="bg-background/50 backdrop-blur-sm p-6 rounded-xl border border-[#0A74FF]/10 shadow-lg hover:shadow-xl transition-all duration-300">
              <img
                src="/MdbApp QR Code Dark.png"
                alt="MdbApp - Your Premier Token Creation Platform. Visit mdbapp.io for secure, automated crypto token deployment. #mdbapp #crypto #tokenCreation"
                className="w-48 h-48 object-contain hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Scan to visit MdbApp.io - Your Trusted Token Creation Platform
            </p>
            <Button
              onClick={handleShare}
              variant="outline"
              className="mt-4 flex items-center gap-2 bg-[#0A74FF]/10 hover:bg-[#0A74FF]/20 text-[#0A74FF] border-[#0A74FF]/20"
            >
              <Share2 className="w-4 h-4" />
              Share MdbApp
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
